// Platform (superadmin only): leads from tallerza.cl, demo requests and the
// waiting list, written by the `contacto` Function. Follow-up is only the
// `estado` field (firestore.rules), plus the conversion to client through the
// `convertirContacto` callable; filters live in the URL.
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Inbox } from 'lucide-react';
import { useMemo } from 'react';
import { useContactos } from '@/datos/consultas';
import { exigir } from '@/lib/guardias';
import { useEsEscritorio } from '@/lib/medios';
import { permisos } from '@/lib/sesion';
import { ListaContactosMovil, TablaContactos } from '@/plataforma/TablaContactos';
import {
  busquedaContactos,
  cumpleEstado,
  ESTADO_POR_DEFECTO,
  filtrarContactos,
  ordenarContactos,
  type FiltroEstado,
  type FiltrosContactos,
} from '@/plataforma/contactos';
import { ErrorCarga, Esqueleto, Segmentado, Tarjeta, Vacio } from '@/ui/basicos';
import { Boton } from '@/ui/boton';
import { Etiqueta, Selector } from '@/ui/campo';
import { Contenedor } from '@/ui/pagina';

export const Route = createFileRoute('/_app/plataforma/contactos')({
  validateSearch: busquedaContactos,
  beforeLoad: exigir(permisos.plataforma),
  component: Pagina,
});

type FiltroTipo = 'todos' | NonNullable<FiltrosContactos['tipo']>;

const opcionesEstado: { valor: FiltroEstado; texto: string }[] = [
  { valor: 'activos', texto: 'Oportunidades abiertas' },
  { valor: 'nuevo', texto: 'Nuevos' },
  { valor: 'contactado', texto: 'Contactados' },
  { valor: 'demo', texto: 'Con demo' },
  { valor: 'piloto', texto: 'En prueba gratis' },
  { valor: 'cliente', texto: 'Clientes' },
  { valor: 'perdido', texto: 'Perdidos' },
  { valor: 'descartado', texto: 'Descartados' },
  { valor: 'todos', texto: 'Todos' },
];

function Pagina() {
  const q = useContactos();
  const filtros = Route.useSearch();
  const navegar = useNavigate({ from: Route.fullPath });
  const escritorio = useEsEscritorio();
  const estado = filtros.estado ?? ESTADO_POR_DEFECTO;

  const todos = useMemo(() => ordenarContactos(q.data ?? []), [q.data]);
  const filas = useMemo(() => filtrarContactos(todos, filtros), [todos, filtros]);
  // Counts per type under the current status filter (what each tab would show).
  const porTipo = useMemo(() => {
    const visibles = todos.filter((c) => cumpleEstado(c, filtros.estado));
    return {
      todos: visibles.length,
      demo: visibles.filter((c) => c.tipo === 'demo').length,
      espera: visibles.filter((c) => c.tipo === 'espera').length,
    };
  }, [todos, filtros.estado]);
  const nuevos = todos.filter((c) => c.estado === 'nuevo').length;

  const fijar = (cambio: Partial<FiltrosContactos>) =>
    void navegar({ search: (s) => ({ ...s, ...cambio }), replace: true });

  return (
    <Contenedor>
      <div>
        <h1 className="titulo text-[24px] leading-tight">Contactos</h1>
        <p className="text-[15px] text-text-2">
          {q.data
            ? `${nuevos === 0 ? 'Sin contactos nuevos' : nuevos === 1 ? '1 contacto nuevo' : `${nuevos} contactos nuevos`} · demos y lista de espera de tallerza.cl`
            : 'Cargando…'}
        </p>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <Segmentado<FiltroTipo>
          etiqueta="Tipo de contacto"
          valor={filtros.tipo ?? 'todos'}
          onCambio={(v) => fijar({ tipo: v === 'todos' ? undefined : v })}
          opciones={(
            [
              ['todos', 'Todos'],
              ['demo', 'Demo'],
              ['espera', 'Lista de espera'],
            ] as const
          ).map(([valor, texto]) =>
            q.data ? { valor, texto, cantidad: porTipo[valor] } : { valor, texto },
          )}
          className="max-w-full overflow-x-auto"
        />
        <div className="flex w-full flex-col gap-1.5 sm:w-60">
          <Etiqueta htmlFor="filtro-estado">Estado</Etiqueta>
          <Selector
            id="filtro-estado"
            value={estado}
            onChange={(e) => {
              const v = e.target.value as FiltroEstado;
              fijar({ estado: v === ESTADO_POR_DEFECTO ? undefined : v });
            }}
            className="h-[38px] text-[14px]"
          >
            {opcionesEstado.map((o) => (
              <option key={o.valor} value={o.valor}>
                {o.texto}
              </option>
            ))}
          </Selector>
        </div>
      </div>

      {q.isError ? (
        <ErrorCarga error={q.error} />
      ) : !q.data ? (
        <Tarjeta
          className="flex flex-col gap-3 p-5"
          aria-busy="true"
          aria-label="Cargando contactos"
        >
          <Esqueleto className="h-12" />
          <Esqueleto className="h-12" />
          <Esqueleto className="h-12" />
        </Tarjeta>
      ) : todos.length === 0 ? (
        <Tarjeta>
          <Vacio icono={<Inbox />} titulo="Aún no llegan contactos">
            Cuando alguien pida una demo o se sume a la lista de espera en tallerza.cl, aparece aquí
            al instante.
          </Vacio>
        </Tarjeta>
      ) : filas.length === 0 ? (
        <Tarjeta>
          <Vacio
            icono={<Inbox />}
            titulo="No hay contactos con estos filtros"
            accion={
              <Boton onClick={() => fijar({ tipo: undefined, estado: 'todos' })}>
                Ver todos los contactos
              </Boton>
            }
          >
            Hay {todos.length} {todos.length === 1 ? 'contacto' : 'contactos'} en total.
          </Vacio>
        </Tarjeta>
      ) : (
        <Tarjeta className="overflow-hidden">
          {escritorio ? <TablaContactos filas={filas} /> : <ListaContactosMovil filas={filas} />}
        </Tarjeta>
      )}
      <p className="text-[13px] text-text-2">
        Los datos los dejó cada persona en el sitio y solo se usan para responderle. Aquí solo se
        cambia el estado del seguimiento (y el motivo si se perdió); los contactos no se editan ni
        se borran. Marcar a alguien como cliente aplica su cupón y es definitivo. El avance de cada
        cupón (demos, pilotos y clientes) se ve en Cupones.
      </p>
    </Contenedor>
  );
}
